import { Pipe, PipeTransform } from '@angular/core';
import { escapeRegExp, simplifyCompanyName } from '@nk/utils-lib';

/**
 * A pipe that extracts contextual text snippets around specified company names or terms.
 *
 * This pipe is designed to show contextual snippets of text that contain specific company names,
 * making it useful for search results, content previews, or highlighting relevant information
 * in large text blocks.
 *
 * The output is PLAIN TEXT (no HTML markup). It does NOT escape its input, so when rendered via
 * `[innerHTML]` it must be piped through `highlightText` (which HTML-escapes) or otherwise escaped
 * first — do not bind raw `skipGram` output to `[innerHTML]` on its own.
 *
 * @example
 * ```html
 * <!-- Render via highlightText, which escapes the snippet and highlights the terms -->
 * <div [innerHTML]="content | skipGram: companyNames | highlightText: companyNames"></div>
 *
 * <!-- As plain text, interpolation escapes automatically -->
 * <span>{{ content | skipGram: companyNames }}</span>
 *
 * <!-- Get match count only -->
 * <span>{{ content | skipGram: companyNames: true: 160: true }}</span>
 * ```
 */
@Pipe({
  name: 'skipGram',
  standalone: false,
})
export class SkipGramPipe implements PipeTransform {
  /**
   * Transforms content by extracting snippets around specified names/terms.
   *
   * @param content - The full text content to search within
   * @param names - Array of company names or terms to search for (will be simplified and escaped)
   * @param showMultipleMatches - Whether to show all matches (true) or just the first one (false)
   * @param length - The length of text to show around each match (default: 160 characters)
   * @param returnCount - If true, returns only the count of matches as a string
   * @param lengthIsPerMatch - If true, length applies to each match; if false, total length is divided among matches
   * @returns Plain-text snippet string (truncations marked with " [...]"), or the match count
   *
   * @example
   * ```typescript
   * // Input content: "This is a long text about Company ABC and Company XYZ..."
   * // Names: ["Company ABC", "Company XYZ"]
   * // Output: "...long text about Company ABC and Company XYZ..."
   * ```
   */
  public transform(
    content: string,
    names: string[],
    showMultipleMatches = true,
    length = 160,
    returnCount = false,
    lengthIsPerMatch = true,
  ): string {
    let output = '';
    let count = 0;
    let matches = null;
    let contentParts = [];
    if (names && names.length > 0) {
      names = names.map((name) => escapeRegExp(simplifyCompanyName(name)));
      const splitRegex = new RegExp(names.join('|'), 'gi');
      matches = content.match(splitRegex);
      contentParts = content.split(splitRegex);

      if (matches) {
        count = matches.length;
      }

      if (!showMultipleMatches && matches) {
        matches = matches.slice(0, 1);
      }
      if (matches && matches.length && lengthIsPerMatch === false) {
        length = length / matches.length;
      }
    }
    if (returnCount) {
      return String(count);
    }

    if (matches) {
      const matchLength = matches.length;
      for (let i = 0; i < matchLength; i++) {
        const match = matches[i];
        let prevPart = contentParts[i];
        const isFirst = i === 0;
        if (isFirst || prevPart.length > 2 * length) {
          const prevPartIndex = prevPart.length - length;
          if (prevPartIndex > 0) {
            prevPart = '[...] ' + prevPart.slice(prevPartIndex);
          }
        }
        output += prevPart + match;
        const nextPart = contentParts[i + 1];
        const isLast = i === matches.length - 1;
        const isTwiceTheSize = nextPart.length > 2 * length;
        if (isLast || isTwiceTheSize) {
          output += nextPart.slice(0, length);
          if (nextPart.length > length) {
            output += ` [...]\n`;
          }
        }
      }
      if (matches && showMultipleMatches === false && count > 1) {
        output += `[x${count}]`;
      }
    } else {
      output = content.slice(0, length * 2);
      if (content.length > length) {
        output += ` [...]\n`;
      }
    }

    return output;
  }
}
